import FilterListIcon from "@mui/icons-material/FilterList";
import { Box, useTheme } from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import * as React from "react";

import { useAuth } from "@/providers";
import { RobotoButton } from "@/shared/components";
import { CollectionRecord } from "@/shared/domain/collections";
import { OrgRecord } from "@/shared/domain/orgs";
import { usePaginatedAPICall } from "@/shared/services/apiHooks";
import { RobotoAPICall, SearchQueryBody } from "@/types";

import CollectionsGrid from "./CollectionsGrid";
import CollectionsTable from "./CollectionsTable";
import CollectionsViewToggle from "./CollectionsViewToggle";
import { CreateCollectionPopover } from "./CreateCollectionPopover";
import { View } from "./types";

interface CollectionsViewProps {
  org: OrgRecord | null;
}

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const INITIAL_ROWS_PER_PAGE = ROWS_PER_PAGE_OPTIONS[1];

const viewToggleStyles = { flex: "0 0 auto", alignSelf: "flex-start" };

/**
 * A paginated list of collections, fetched from a parameterized endpoint.
 * The list can be displayed as a compact table (soon) or a card grid.
 */
export function CollectionsView({ org }: CollectionsViewProps) {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [page, setPage] = React.useState<number>(0);
  const [view, setView] = React.useState<View>(View.Table);
  const [showUserCollections, setShowUserCollections] =
    React.useState<boolean>(false);

  const [rowsPerPage, setRowsPerPage] = React.useState<number>(
    INITIAL_ROWS_PER_PAGE,
  );

  const {
    cacheLength,
    getFirstPage,
    fetchNextPage,
    fetchPreviousPage,
    isNextPageAvailable,
    loading,
    onRowsPerPageChange,
    pageData,
    //removeItemFromCache,
  } = usePaginatedAPICall<CollectionRecord>();

  const fetchCollections = React.useCallback(() => {
    const baseEndpoint = "/collections/search";
    const requestBody: SearchQueryBody = {
      sort_direction: "DESC",
      sort_by: "created",
    };

    if (showUserCollections && currentUser?.user_id) {
      requestBody.condition = {
        field: "created_by",
        comparator: "EQUALS",
        value: currentUser?.user_id,
      };
    }

    const queryParams = new URLSearchParams();
    queryParams.set("content_mode", "references");

    const request: RobotoAPICall = {
      endpoint: () => {
        return baseEndpoint;
      },
      method: "POST",
      requestBody: JSON.stringify(requestBody),
      queryParams: queryParams,
      orgId: org?.org_id,
    };

    void getFirstPage(request, INITIAL_ROWS_PER_PAGE);
  }, [getFirstPage, org?.org_id, currentUser?.user_id, showUserCollections]);

  // On mount, fetch the first page of collections
  React.useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const onPageChange = async (newPage: number) => {
    if (newPage > page) {
      setPage(page + 1);
      await fetchNextPage(newPage, rowsPerPage);
    } else {
      setPage(page - 1);
      fetchPreviousPage(newPage, rowsPerPage);
    }
  };

  const onRowsPerPageChangeHandler = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let val = parseInt(e?.target?.value);
    if (!Number.isFinite(val)) {
      val = ROWS_PER_PAGE_OPTIONS[0];
    }
    setPage(0);
    setRowsPerPage(val);
    await onRowsPerPageChange(val);
  };

  const onShowUserCollections = () => {
    setPage(0);
    setShowUserCollections((prev) => !prev);
  };

  const collectionsView =
    view === View.CardGrid ? (
      <CollectionsGrid collections={pageData} isLoading={loading} />
    ) : (
      <CollectionsTable collections={pageData} isLoading={loading} />
    );

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        marginBottom={theme.spacing(3)}
      >
        <Box
          sx={{
            display: "flex",
            gap: theme.spacing(2),
            overflow: "hidden",
          }}
        >
          <CreateCollectionPopover />

          <RobotoButton
            eventName={"MyCollectionsClicked"}
            variant={showUserCollections ? "contained" : "outlined"}
            size="small"
            color="primary"
            startIcon={<FilterListIcon />}
            onClick={onShowUserCollections}
            sx={{
              height: theme.actionButtonHeight,
              minWidth: 80,
            }}
          >
            My Collections
          </RobotoButton>
        </Box>

        <CollectionsViewToggle
          setView={setView}
          sx={viewToggleStyles}
          view={view}
        />
      </Box>

      <Box width="100%">
        {collectionsView}

        <TablePagination
          component="div"
          count={isNextPageAvailable ? -1 : cacheLength}
          nextIconButtonProps={{
            disabled: !isNextPageAvailable,
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          page={page}
          onPageChange={(_, newPage) => {
            void onPageChange(newPage);
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            void onRowsPerPageChangeHandler(e);
          }}
          labelRowsPerPage="Collections per page:"
        />
      </Box>
    </>
  );
}
